import React, { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useParams } from "react-router-dom";

interface BorrowerDetails {
  loanId: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  mediationDate: string;
  mediationTime: string;
  OS: string;
  loanAmount: string;
}

interface ApiResponse {
  data: BorrowerDetails;
  status: number;
  message: string;
}

type IconName =
  | "file"
  | "mail"
  | "phone"
  | "mapPin"
  | "calendar"
  | "clock"
  | "check"
  | "shield"
  | "wallet"
  | "arrowRight";

const icons: Record<IconName, ReactNode> = {
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </>
  ),
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-10 7L2 6" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  mapPin: (
    <>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </>
  ),
  check: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
  shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />,
  wallet: (
    <>
      <path d="M21 12V7H5a2 2 0 0 1 0-4h14v4" />
      <path d="M3 5v14a2 2 0 0 0 2 2h16v-5" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </>
  ),
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </>
  ),
};

interface IconProps {
  name: IconName;
  className?: string;
}

const Icon = ({ name, className = "" }: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    width={16}
    height={16}
  >
    {icons[name]}
  </svg>
);

interface FieldProps {
  icon: IconName;
  label: string;
  value?: string;
}

const Field = ({ icon, label, value }: FieldProps) => (
  <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-b-0">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
      <Icon name={icon} />
    </div>

    <div className="min-w-0">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
        {label}
      </p>

      <p className="mt-0.5 text-sm font-medium text-slate-800 break-words">
        {value || "N/A"}
      </p>
    </div>
  </div>
);

export default function MediationConfirmation() {
  const { loanId } = useParams<{ loanId: string }>();
  const [borrowerDetails, setBorrowerDetails] =
    useState<BorrowerDetails | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  /**
   * Fetch borrower details
   */
  useEffect(() => {
    const fetchBorrowerDetails = async () => {
      if (!loanId) {
        setError("Loan ID is missing from URL.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `https://app.solveesy.com/v1/public/borrower-details?loanId=${encodeURIComponent(
            loanId
          )}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) throw new Error("Unauthorized request.");
          if (response.status === 403) throw new Error("Access forbidden.");
          if (response.status === 404) throw new Error("Borrower details not found.");
          throw new Error("Failed to fetch borrower details.");
        }

        const result: ApiResponse = await response.json();

        if (!result.data) {
          throw new Error("Borrower details are not available.");
        }

        setBorrowerDetails(result.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Something went wrong.");
      } finally {
        setLoading(false);
      }
    };

    fetchBorrowerDetails();
  }, [loanId]);

  /**
   * Confirm mediation
   */
  const handleConfirm = async () => {
    if (!loanId) {
      setError("Loan ID is missing.");
      return;
    }

    try {
      setConfirming(true);
      setError("");

      const response = await fetch(
        `https://app.solveesy.com/v1/public/send-conformation?loanId=${encodeURIComponent(
          loanId
        )}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) throw new Error("Unauthorized request.");
        if (response.status === 403) throw new Error("You are not allowed to confirm this mediation.");
        if (response.status === 404) throw new Error("Loan not found.");
        throw new Error("Failed to confirm mediation.");
      }

      setConfirmed(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong while confirming.");
    } finally {
      setConfirming(false);
    }
  };

  /**
   * Loading state
   */
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-3 h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-slate-900" />
          <p className="text-sm text-slate-500">Loading borrower details...</p>
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error && !borrowerDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="w-full max-w-md rounded-xl border border-red-200 bg-white p-6 text-center shadow-sm">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
            !
          </div>
          <h2 className="text-base font-semibold text-slate-900">Unable to load details</h2>
          <p className="mt-2 text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!borrowerDetails) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/60">
          {/* Header */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 px-6 py-5">
            <div className="flex items-center gap-2 text-slate-300">
              <Icon name="shield" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                Mediation invite
              </span>
            </div>

            <h2 className="mt-1.5 text-lg font-semibold text-white">
              Confirm your participation
            </h2>

            <p className="mt-0.5 text-sm text-slate-400">
              Loan Id: {borrowerDetails.loanId}
            </p>
          </div>

          {/* Borrower Details */}
          <div className="px-6 py-4">
            <Field icon="file" label="Borrower name" value={borrowerDetails.name} />
            <Field
              icon="wallet"
              label="Loan Amount"
              value={`₹${borrowerDetails.loanAmount}`}
            />
            <Field icon="wallet" label="Outstanding" value={`₹${borrowerDetails.OS}`} />
          </div>

          {/* Schedule */}
          <div className="mx-6 mb-1 rounded-xl bg-blue-50/70 px-4 py-3.5 border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
                <Icon name="calendar" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-500">Mediation date</p>
                <p className="text-sm font-semibold text-blue-900">
                  {borrowerDetails.mediationDate}
                </p>
              </div>
            </div>

            <div className="mt-2.5 flex items-center gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-blue-600 shadow-sm">
                <Icon name="clock" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-500">Time slot</p>
                <p className="text-sm font-semibold text-blue-900">
                  {borrowerDetails.mediationTime}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-5">
            {confirmed ? (
              <div className="flex items-center justify-center gap-2 rounded-lg bg-green-50 py-3 text-sm font-medium text-green-700 border border-green-100">
                <Icon name="check" />
                You're confirmed for mediation
              </div>
            ) : (
              <>
                <button
                  onClick={handleConfirm}
                  disabled={confirming}
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 py-3.5 text-sm font-semibold text-white shadow-md shadow-blue-600/25 ring-2 ring-blue-600/20 ring-offset-2 transition hover:bg-blue-700 hover:shadow-lg active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {confirming ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Confirming...
                    </>
                  ) : (
                    <>
                      Confirm participation
                      <Icon name="arrowRight" />
                    </>
                  )}
                </button>

                {error && (
                  <p className="mt-2 text-center text-xs text-red-500">{error}</p>
                )}
              </>
            )}

            <p className="mt-3 text-center text-xs text-slate-400">
              By confirming, you agree to attend the scheduled mediation session.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}